import { useState, useRef, useEffect, useLayoutEffect, memo } from 'react';
import { Plus, Minus, Send, Pencil, Trash2, Check, X, Image as ImageIcon, Maximize2, Pin, ArrowUpRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import Avatar from './Avatar';
import { linkify } from '../utils';

const ChatEdit = ({ msg, onSave, onCancel }) => {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) {
      ref.current.focus();
      // Move cursor to end
      const range = document.createRange();
      const sel = window.getSelection();
      range.selectNodeContents(ref.current);
      range.collapse(false);
      sel.removeAllRanges();
      sel.addRange(range);
    }
  }, []);

  return (
    <div className="flex flex-col gap-2 w-full bg-white p-2 rounded-lg border border-zinc-200">
      <div
        ref={ref}
        contentEditable
        dangerouslySetInnerHTML={{ __html: msg.text }}
        className="w-full text-sm outline-none min-h-[60px]"
      />
      <div className="flex gap-2">
        <button
          onClick={() => onSave(msg.id, ref.current.innerHTML)}
          title="Save changes"
          aria-label="Save changes"
          className="flex items-center gap-1 text-green-700 bg-green-50 hover:bg-green-100 px-2 py-1 rounded transition-colors text-xs font-bold uppercase tracking-wider"
        >
          <Check size={14} strokeWidth={2.5} /> Save
        </button>
        <button
          onClick={onCancel}
          title="Cancel edit"
          aria-label="Cancel edit"
          className="flex items-center gap-1 text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100 px-2 py-1 rounded transition-colors text-xs font-bold uppercase tracking-wider"
        >
          <X size={14} strokeWidth={2.5} /> Cancel
        </button>
      </div>
    </div>
  );
};

const CorndogIcon = ({ size = 16, className = '' }) => (
  <div
    className={`relative flex items-center justify-center ${className}`}
    style={{ width: size, height: size }}
  >
    <svg
      viewBox="0 0 24 24"
      fill="none"
      className="w-full h-full"
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Stick */}
      <path d="M12 18V22" stroke="#71717a" strokeWidth="2" strokeLinecap="round" />
      {/* Plain Batter (Golden Brown) */}
      <rect x="8" y="3" width="8" height="16" rx="4" fill="#b45309" />
    </svg>
  </div>
);

const ChatView = ({
  path,
  isFullWidth,
  label = 'CHAT',
  user,
  channels,
  activeChannelId,
  currentView,
  dispatch,
  allUsers,
  messages,
  editingMessageId,
  isChatOpen,
  reactionFilter,
  activeColorHex,
  chatInputHtml,
  handleSendMessage,
  handleSaveEditMessage,
  handleToggleReaction,
  handleDeleteMessage,
  handleTogglePinMessage,
  handleScrollToMessage,
  handleStartDM,
  getDmUser,
  appId,
  username,
  userAvatar,
  customHeader = null,
  handleLoadMore,
  messageLimit,
  handleShowProfile,
  isDM: propIsDM,
  isMobile,
  isTouch,
  onClose,
  handleBackgroundUpload,
  b2Token,
  onHeaderMouseDown
}) => {
  const navigate = useNavigate();
  const chatContainerRef = useRef(null);
  const chatEndRef = useRef(null);
  const chatInputRef = useRef(null);
  const fileInputRef = useRef(null);
  const [hoveredMessageId, setHoveredMessageId] = useState(null);
  const [hoveredUser, setHoveredUser] = useState(null);
  const hoverTimeoutRef = useRef(null);
  const [isUploading, setIsUploading] = useState(false);
  const [tappedMessageId, setTappedMessageId] = useState(null);

  const formatMessageContent = (content) => {
    if (!content) return content;
    let html = content.replace(/f005\.backblazeb2\.com/g, 'f000.backblazeb2.com');
    if (b2Token) {
      html = html.replace(/(src="https:\/\/f000\.backblazeb2\.com[^"?]+)(?:")?([^>]*>)/g, `$1?Authorization=${b2Token}"$2`);
      html = html.replace(/(window\.open\('https:\/\/f000\.backblazeb2\.com[^'?]+)(?:')?([^)]*\))/g, `$1?Authorization=${b2Token}'$2`);
    }
    return linkify(html);
  };

  const useTapMenu = isMobile || isTouch;

  const [size, setSize] = useState(parseInt(localStorage.getItem(`chatFontSize_${appId}`) || '13'));
  const [localInputHtml, setLocalInputHtml] = useState('');

  const REACTION_OPTIONS = ['❤️', '🕳️', '⭐', 'corndog'];
  const renderReaction = (id, iconSize = 14) => {
    if (id === 'corndog') return <CorndogIcon size={iconSize} />;
    return id;
  };

  useEffect(() => {
    localStorage.setItem(`chatFontSize_${appId}`, size.toString());
  }, [size, appId]);

  useLayoutEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'auto' });
    }
    
    if (chatContainerRef.current) {
      const imgs = chatContainerRef.current.querySelectorAll('img');
      imgs.forEach((img) => {
        if (!img.dataset.scrollListener) {
          img.dataset.scrollListener = 'true';
          img.addEventListener('load', () => {
            chatEndRef.current?.scrollIntoView({ behavior: 'auto' });
          });
        }
      });
    }
  }, [messages]);

  useEffect(() => {
    const handleResize = () => {
      if (chatEndRef.current) {
        chatEndRef.current.scrollIntoView({ behavior: 'auto' });
      }
    };

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleResize);
    }
    return () => {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleResize);
      }
    };
  }, []);

  const activeChannel = (channels || []).find((c) => c.id === activeChannelId) || { name: label };
  const isDM = propIsDM !== undefined ? propIsDM : activeChannel?.type === 'dm';
  const dmTargetUser = isDM
    ? getDmUser(activeChannel) ||
      (allUsers || []).find(
        (u) => (activeChannel.participants || []).includes(u.id) && u.id !== user?.uid
      )
    : null;
   
  const isTargetOnline =
    dmTargetUser &&
    dmTargetUser.lastSeen?.seconds * 1000 > Date.now() /* eslint-disable-line react-hooks/purity */ - 5 * 60 * 1000;
  const dmLabel = dmTargetUser?.name || 'Direct Message';
  const isGeneral = activeChannelId === 'general_chat_global';
  const isWorkspace = currentView === 'workspace';

  const headerStyle = {
    backgroundColor: isGeneral ? '#22c55e1A' : activeColorHex + '0D',
    borderColor: isGeneral ? '#22c55e4D' : activeColorHex + '33',
  };
  const headerTitleClass = isGeneral ? 'text-green-900' : 'text-zinc-800';
  const containerClass = `flex flex-col h-full bg-white transition-all duration-500 overflow-x-hidden relative ${isFullWidth ? 'w-full' : 'flex-1 min-w-0'}`;
  const containerStyle = {
    borderLeft: !isFullWidth
      ? `1px solid ${isGeneral ? '#22c55e4D' : activeColorHex + '26'}`
      : 'none',
  };

  const inputAreaClass = `p-4 border-t transition-all duration-500 ${isGeneral ? 'border-green-200 bg-green-50/20' : ''}`;
  const inputAreaStyle = !isGeneral
    ? { borderTopColor: activeColorHex + '33', backgroundColor: activeColorHex + '08' }
    : {};
  const placeholderText = isGeneral
    ? 'Message the patrons...'
    : `Message ${isDM ? dmTargetUser?.name || 'Private Message' : activeChannel.name}...`;
  const placeholderColor = isGeneral ? 'before:text-zinc-400' : 'before:text-zinc-500/50';

  const handlePaste = (e) => {
    const items = e.clipboardData.items;
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.indexOf('image') !== -1) {
        const file = items[i].getAsFile();
        handleImageUpload(file);
      }
    }
  };

  const handleImageUpload = async (file, insertToInput = false) => {
    if (!file || !handleSendMessage || !handleBackgroundUpload) return;
    setIsUploading(true);
    try {
      await new Promise((resolve) => {
        handleBackgroundUpload(file, activeChannelId || 'global', 'photo', {
          onComplete: async (url) => {
            const imgHtml = `<img src="${url}" class="max-w-xs rounded-lg shadow-sm cursor-pointer" onclick="window.open('${url}', '_blank')" />`;
            if (insertToInput && chatInputRef.current) {
              chatInputRef.current.innerHTML += imgHtml;
              dispatch({ type: 'SET_CHAT_INPUT', payload: chatInputRef.current.innerHTML });
              chatInputRef.current.focus();
            } else {
              await handleSendMessage(imgHtml);
            }
            resolve();
          }
        });
      });
    } catch (err) {
      console.error(err);
    } finally {
      setIsUploading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const isTargetInput = e.target.closest('[contenteditable="true"]');
    const files = e.dataTransfer.files;
    if (files && files[0] && files[0].type.startsWith('image/')) {
      handleImageUpload(files[0], !!isTargetInput);
    }
  };

  const formatTimestamp = (ts) => {
    const date = new Date(ts);
    const now = new Date();
    const diffMs = now - date;
    const diffMin = Math.floor(diffMs / (1000 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const isToday = date.toDateString() === now.toDateString();

    if (diffMin < 60 && diffMin >= 0) {
      if (diffMin === 0) return 'Just now';
      return `${diffMin} min`;
    }

    if (isToday) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }

    if (diffDays < 7) {
      // Show "Friday 2:25pm"
      const dayName = date.toLocaleDateString([], { weekday: 'long' });
      const timeStr = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      return `${dayName} ${timeStr}`;
    }

    // Show "March 13th 2026"
    return date.toLocaleDateString([], { month: 'long', day: 'numeric', year: 'numeric' });
  };

  return (
    <div data-testid="chat-view" className={containerClass} style={containerStyle}>
      <header
        onMouseDown={onHeaderMouseDown}
        data-testid="chat-header"
        className={`h-10 px-4 border-b flex items-center justify-between flex-shrink-0 transition-all duration-500 sticky top-0 z-30 ${onHeaderMouseDown ? 'cursor-move' : ''} ${isGeneral || (isWorkspace && !isDM) || isDM ? 'border-b-2' : ''}`}
        style={headerStyle}
      >
        <div className="flex items-center gap-4 flex-1 min-w-0 overflow-hidden">
          {customHeader ? (
            customHeader
          ) : (
            <div className="flex items-center gap-2">
              {isDM && dmTargetUser && (
                <button
                  onClick={() => handleShowProfile?.(dmTargetUser)}
                  className="flex items-center gap-2 mr-1 hover:opacity-80 transition-opacity"
                >
                  <Avatar src={dmTargetUser.avatar} size={20} />
                  {isTargetOnline && (
                    <div className="w-1.5 h-1.5 rounded-full bg-green-500 shadow-[0_0_5px_rgba(34,197,94,0.5)] animate-pulse" />
                  )}
                </button>
              )}
              <h2
                data-testid="tiki-bar-title"
                className={`text-[10px] uppercase tracking-widest transition-colors duration-500 flex-shrink-0 ${headerTitleClass}`}
              >
                {isGeneral ? 'Tiki Bar' : isDM ? <span>{dmLabel}</span> : label}
              </h2>
              {path && (
                <span className="text-[10px] text-blue-500 font-normal tracking-normal truncate">
                  NYTM{path.map((p) => ` > ${p.name}`).join('')}
                </span>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center gap-1 border-l border-zinc-200 pl-3 flex-shrink-0">
          <button
            data-testid="decrease-font-btn"
            onClick={() => setSize(Math.max(8, size - 1))}
            className="p-1 text-zinc-400 hover:text-zinc-900 transition-colors"
          >
            <Minus size={14} strokeWidth={2.5} />
          </button>
          <span className="text-[10px] text-zinc-500 font-bold w-4 text-center">{size}</span>
          <button
            data-testid="increase-font-btn"
            onClick={() => setSize(Math.min(32, size + 1))}
            className="p-1 text-zinc-400 hover:text-zinc-900 transition-colors"
          >
            <Plus size={14} strokeWidth={2.5} />
          </button>
        </div>
        <div className="flex items-center gap-1 border-l border-zinc-200 pl-3">
          {REACTION_OPTIONS.map((emoji) => (
            <button
              key={emoji}
              data-testid={`filter-${emoji}`}
              onClick={() =>
                dispatch({
                  type: 'SET_REACTION_FILTER',
                  payload: reactionFilter === emoji ? null : emoji,
                })
              }
              className={`w-6 h-6 flex items-center justify-center rounded transition-all text-xs ${reactionFilter === emoji ? 'bg-white shadow-sm scale-110 border border-zinc-300' : 'hover:bg-black/5'}`}
            >
              {renderReaction(emoji, 14)}
            </button>
          ))}
          {isDM && !customHeader && !isMobile && (
            <button
              onClick={() => {
                dispatch({ type: 'SET_OPEN_DM', payload: activeChannelId });
                navigate('/');
              }}
              className="ml-2 px-2 py-1 flex items-center gap-1 text-zinc-500 hover:text-zinc-900 hover:bg-black/5 rounded transition-colors"
            >
              <ArrowUpRight size={14} strokeWidth={2.5} />
              <span className="text-xs font-bold uppercase tracking-wider">Pop Out</span>
            </button>
          )}
          {isDM && customHeader && !isMobile && (
            <button
              onClick={() => {
                navigate(`/p/${activeChannelId}/chat${window.location.search}`);
                if (onClose) onClose();
              }}
              title="Open in Main View"
              className="ml-1 p-1 text-zinc-400 hover:text-zinc-900 hover:bg-black/5 rounded transition-colors"
            >
              <Maximize2 size={14} strokeWidth={2.5} />
            </button>
          )}
          {onClose && (
            <button
              onClick={onClose}
              title="Close"
              className="ml-1 p-1 text-zinc-400 hover:text-zinc-900 hover:bg-black/5 rounded transition-colors"
            >
              <X size={14} strokeWidth={2.5} />
            </button>
          )}
        </div>
      </header>

      {activeChannel?.pinnedMessageId && (
        <div 
          onClick={() => handleScrollToMessage && handleScrollToMessage(activeChannel.pinnedMessageId)}
          className="bg-blue-50/50 border-b border-blue-100 px-4 py-2 flex items-center justify-center gap-2 cursor-pointer hover:bg-blue-50 transition-colors shrink-0 z-20"
        >
          <Pin size={10} className="text-blue-500 shrink-0" strokeWidth={3} />
          {(() => {
            const pMsg = messages.find(m => m.id === activeChannel.pinnedMessageId);
            const pText = pMsg?.text || activeChannel.pinnedMessageText || 'Pinned Message';
            let senderName = 'Unknown';
            if (pMsg) {
              const isMe = pMsg.userId === user?.uid;
              const sender = (allUsers || []).find((u) => u.id === pMsg.userId);
              senderName = isMe ? username || 'Me' : sender?.name || pMsg.userName || 'User';
            }
            return (
              <div className="flex items-center min-w-0 flex-1 gap-1">
                <span className="text-[11px] font-bold text-blue-700 shrink-0">
                  {senderName}:
                </span>
                <div 
                  className="text-[11px] italic text-blue-600 truncate max-w-full" 
                  dangerouslySetInnerHTML={{ __html: formatMessageContent(pText) }} 
                />
              </div>
            );
          })()}
        </div>
      )}

      <div
        ref={chatContainerRef}
        className="flex-1 overflow-y-auto p-4 space-y-4 min-h-0 bg-transparent"
      >
        {messages.length >= messageLimit && (
          <button
            onClick={handleLoadMore}
            className="w-full py-2 text-[10px] font-black uppercase tracking-widest text-zinc-400 hover:text-zinc-600 transition-colors"
          >
            Load older messages
          </button>
        )}
        <div className="flex flex-col gap-4">
          {(() => {
            const filteredMessages = messages.filter(
              (m) =>
                !reactionFilter ||
                (m.reactions &&
                  m.reactions[reactionFilter] &&
                  m.reactions[reactionFilter].length > 0)
            );
            return filteredMessages.map((msg, idx) => {
              const isMe = msg.userId === user?.uid;
              const sender = (allUsers || []).find((u) => u.id === msg.userId);
              const senderName = isMe ? username || 'Me' : sender?.name || msg.userName || 'User';
              const prevMsg = filteredMessages[idx - 1];
              const showHeader =
                !prevMsg ||
                prevMsg.userId !== msg.userId ||
                msg.createdAt?.seconds - prevMsg.createdAt?.seconds > 300;
              const isEditing = editingMessageId === msg.id;
               
              const currTs = msg.createdAt?.toMillis
                ? msg.createdAt.toMillis()
                : msg.createdAt?.seconds * 1000 ||
                  msg.clientTimestamp ||
                  Date.now(); /* eslint-disable-line react-hooks/purity */

              return (
                <div
                  key={msg.id}
                  id={`message-${msg.id}`}
                  data-testid="message-item"
                  className={`flex flex-col ${isMe ? 'items-end' : 'items-start'} group`}
                  style={{ contentVisibility: 'auto', containIntrinsicSize: '0 40px' }}
                >
                  {showHeader && (
                    <div
                      className={`flex items-center gap-2 mb-1 px-1 ${isMe ? 'flex-row-reverse' : ''}`}
                    >
                      <span
                        className="text-[10px] font-black uppercase tracking-tighter text-zinc-400 cursor-pointer hover:text-zinc-900 transition-colors"
                        onMouseEnter={(e) => {
                          if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
                          hoverTimeoutRef.current = setTimeout(() => {
                            setHoveredUser(
                              sender || { id: msg.userId, name: senderName, avatar: msg.userAvatar }
                            );
                            setHoveredMessageId(msg.id);
                          }, 500);
                        }}
                        onMouseLeave={() => {
                          if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
                          setHoveredUser(null);
                          setHoveredMessageId(null);
                        }}
                        onClick={() =>
                          handleShowProfile(
                            sender || { id: msg.userId, name: senderName, avatar: msg.userAvatar }
                          )
                        }
                      >
                        {senderName}
                      </span>
                      <span className="text-zinc-300 font-bold text-[9px]">
                        {formatTimestamp(currTs)}
                      </span>
                    </div>
                  )}
                  <div
                    className={`flex items-start gap-2 max-w-[85%] ${isMe ? 'flex-row-reverse' : ''}`}
                  >
                    {showHeader && (
                      <div
                        onClick={() =>
                          handleShowProfile(
                            sender || { id: msg.userId, name: senderName, avatar: msg.userAvatar }
                          )
                        }
                        className="cursor-pointer hover:opacity-80 transition-opacity"
                      >
                        <Avatar
                          src={isMe ? userAvatar : sender?.avatar || msg.userAvatar}
                          size={28}
                          className="mt-0.5 rounded-xl shadow-sm border border-zinc-100"
                        />
                      </div>
                    )}
                    {!showHeader && <div className="w-7" />}

                    <div className="group/msg relative w-fit max-w-full min-w-0">
                      <div
                        className={`absolute ${idx === 0 ? '-bottom-8' : '-top-7'} flex gap-0.5 items-center bg-white border border-zinc-200 rounded-lg shadow-xl p-1 z-[60] transition-all scale-90 ${isMe ? `right-full mr-2 ${idx === 0 ? 'origin-top-right' : 'origin-bottom-right'}` : `left-4 ${idx === 0 ? 'origin-top-left' : 'origin-bottom-left'}`} ${!useTapMenu ? 'opacity-0 group-hover/msg:opacity-100 group-hover/msg:scale-100' : tappedMessageId === msg.id ? 'opacity-100 scale-100 animate-in fade-in zoom-in duration-100' : 'opacity-0 pointer-events-none'}`}
                      >
                        {REACTION_OPTIONS.map((emoji) => (
                          <button
                            key={emoji}
                            data-testid={`react-${emoji}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleToggleReaction(msg, emoji);
                              if (useTapMenu) setTappedMessageId(null);
                            }}
                            className="p-1 hover:bg-zinc-100 rounded transition-colors"
                          >
                            {renderReaction(emoji, 16)}
                          </button>
                        ))}
                        <div className="w-px h-3 bg-zinc-200 mx-0.5" />
                        <button
                          data-testid="message-pin-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            const isCurrentlyPinned = activeChannel?.pinnedMessageId === msg.id;
                            if (handleTogglePinMessage) handleTogglePinMessage(msg, !isCurrentlyPinned);
                            if (useTapMenu) setTappedMessageId(null);
                          }}
                          className={`p-1 rounded transition-colors ${activeChannel?.pinnedMessageId === msg.id ? 'text-blue-500 hover:text-blue-600 bg-blue-50' : 'text-zinc-400 hover:text-zinc-900 hover:bg-zinc-100'}`}
                          title={activeChannel?.pinnedMessageId === msg.id ? 'Unpin Message' : 'Pin Message'}
                        >
                          <Pin size={12} strokeWidth={2.5} />
                        </button>
                        {isMe && (
                          <>
                            <button
                              data-testid="message-edit-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                dispatch({ type: 'SET_EDIT_MESSAGE', payload: msg.id });
                                if (useTapMenu) setTappedMessageId(null);
                              }}
                              className="text-zinc-400 hover:text-zinc-900 p-1 hover:bg-zinc-100 rounded"
                            >
                              <Pencil size={12} strokeWidth={2.5} />
                            </button>
                            <button
                              data-testid="message-delete-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (!window.confirm("Delete this message? This can't be undone.")) return;
                                handleDeleteMessage(msg.id);
                                if (useTapMenu) setTappedMessageId(null);
                              }}
                              className="text-zinc-400 hover:text-red-600 p-1 hover:bg-red-50 rounded"
                            >
                              <Trash2 size={12} strokeWidth={2.5} />
                            </button>
                          </>
                        )}
                      </div>
                      <div
                        data-testid={`message-text-${msg.id}`}
                        data-is-me={isMe}
                        data-deleted={
                          msg.text === 'This message has been deleted' ? 'true' : 'false'
                        }
                        onClick={() => {
                          if (useTapMenu)
                            setTappedMessageId(tappedMessageId === msg.id ? null : msg.id);
                        }}
                        className={`rounded-2xl px-3 py-1.5 bg-zinc-50 border transition-colors break-words overflow-hidden ${activeChannel?.pinnedMessageId === msg.id ? 'border-blue-300 shadow-[0_0_8px_rgba(59,130,246,0.2)]' : 'border-zinc-100'} ${isEditing ? 'w-full' : 'max-w-full inline-block min-w-0 active:bg-zinc-100'}`}
                      >
                        {activeChannel?.pinnedMessageId === msg.id && (
                          <div className="flex items-center gap-1 text-[9px] font-bold text-blue-500 uppercase tracking-wider mb-1">
                            <Pin size={10} strokeWidth={3} /> Pinned
                          </div>
                        )}
                        {isEditing ? (
                          <ChatEdit
                            msg={msg}
                            onSave={handleSaveEditMessage}
                            onCancel={() => dispatch({ type: 'SET_EDIT_MESSAGE', payload: null })}
                          />
                        ) : (
                          <div className="flex flex-col min-w-0 overflow-hidden">
                            <div
                              data-testid="message-text"
                              className={`leading-snug text-zinc-700 font-light whitespace-pre-wrap break-words ${msg.text === 'This message has been deleted' ? 'italic text-zinc-300' : ''}`}
                              style={{
                                fontSize: `${size}px`,
                                overflowWrap: 'anywhere',
                                wordBreak: 'break-word',
                                display: 'block',
                              }}
                              dangerouslySetInnerHTML={{ __html: formatMessageContent(msg.text) }}
                            />
                            {msg.editedAt && msg.text !== 'This message has been deleted' && (
                              <span className="text-[9px] text-zinc-500 italic">
                                edited{' '}
                                {new Date(msg.editedAt.seconds * 1000).toLocaleTimeString([], {
                                  hour: '2-digit',
                                  minute: '2-digit',
                                })}
                              </span>
                            )}
                            <div className="flex flex-wrap gap-1 mt-1">
                              {msg.reactions &&
                                Object.entries(msg.reactions).map(([emoji, uids]) => {
                                  if (!uids || uids.length === 0) return null;
                                  const names = uids
                                    .map(
                                      (uid) =>
                                        (allUsers || []).find((u) => u.id === uid)?.name ||
                                        (uid === user?.uid ? username || 'Me' : 'User')
                                    )
                                    .join(', ');
                                  return (
                                    <button
                                      key={emoji}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleToggleReaction(msg, emoji);
                                      }}
                                      className={`group/react relative flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] border transition-colors ${uids.includes(user?.uid) ? 'bg-zinc-100 border-zinc-300' : 'bg-white border-zinc-100 hover:bg-zinc-50'}`}
                                    >
                                      {renderReaction(emoji, 10)} <span>{uids.length}</span>
                                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 bg-zinc-800 text-white text-[9px] rounded shadow-lg opacity-0 group-hover/react:opacity-100 pointer-events-none whitespace-nowrap z-50 transition-opacity">
                                        {names}
                                      </div>
                                    </button>
                                  );
                                })}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            });
          })()}
          <div ref={chatEndRef} />
        </div>
      </div>
      <div 
        className="w-full shrink-0 flex flex-col cursor-text"
        onClick={(e) => {
          if (!e.target.closest('button') && !e.target.closest('[contenteditable]')) {
            e.preventDefault();
            chatInputRef.current?.focus({ preventScroll: true });
          }
        }}
        onTouchEnd={(e) => {
          if (!e.target.closest('button') && !e.target.closest('[contenteditable]')) {
            e.preventDefault();
            chatInputRef.current?.focus({ preventScroll: true });
          }
        }}
      >
        <div className={inputAreaClass} style={inputAreaStyle}>
          <div className={`relative flex items-end gap-1.5 ${isFullWidth ? 'max-w-none mx-auto' : ''}`}>
          <input
            type="file"
            ref={fileInputRef}
            className="hidden"
            accept="image/*"
            onChange={(e) => {
              if (e.target.files?.[0]) {
                handleImageUpload(e.target.files[0]);
                e.target.value = ''; // Reset
              }
            }}
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
            className="p-2.5 bg-white border border-zinc-200 rounded-xl text-zinc-400 hover:text-zinc-900 active:bg-zinc-50 transition-all shadow-sm"
          >
            <ImageIcon size={18} strokeWidth={2.5} />
          </button>
          <div className="relative flex-1">
            <div
              ref={chatInputRef}
              data-testid="chat-input"
              contentEditable
              onInput={(e) => {
                const html = e.currentTarget.innerHTML || '';
                setLocalInputHtml(html);
                if (!isDM) dispatch({ type: 'SET_CHAT_INPUT', payload: html });
              }}
              onKeyDown={async (e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  const html = chatInputRef.current.innerHTML;
                  if (!html.trim()) return;

                  // IMMEDIATE ACTION: Clear and refocus
                  chatInputRef.current.innerHTML = '';
                  setLocalInputHtml('');
                  chatInputRef.current.focus({ preventScroll: true });

                  await handleSendMessage(html);
                  if (!isDM) dispatch({ type: 'SET_CHAT_INPUT', payload: '' });
                }
              }}
              onPaste={handlePaste}
              onDrop={handleDrop}
              onDragOver={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onFocus={() => {
                setTimeout(() => {
                  chatEndRef.current?.scrollIntoView({ behavior: 'auto' });
                }, 100);
              }}
              className={`w-full bg-white border border-zinc-200 text-zinc-800 px-3 py-2.5 pr-10 text-sm rounded-xl focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-100 transition-all max-h-96 overflow-y-auto min-h-[40px] whitespace-pre-wrap relative select-text cursor-text empty:before:content-[attr(data-placeholder)] ${placeholderColor} empty:before:pointer-events-none`}
              data-placeholder={placeholderText}
            />
            <button
              onClick={async () => {
                const html = chatInputRef.current.innerHTML;
                if (!html.trim() || isUploading) return;

                // IMMEDIATE ACTION: Clear and refocus
                chatInputRef.current.innerHTML = '';
                setLocalInputHtml('');
                chatInputRef.current.focus({ preventScroll: true });

                await handleSendMessage(html);
                if (!isDM) dispatch({ type: 'SET_CHAT_INPUT', payload: '' });
              }}
              disabled={!localInputHtml.trim() || isUploading}
              className={`absolute right-1.5 top-1/2 -translate-y-1/2 p-1.5 rounded-lg ${!localInputHtml.trim() ? 'text-zinc-300' : 'text-zinc-900 bg-zinc-100'} transition-all ${isUploading ? 'animate-spin' : ''}`}
            >
              {isUploading ? (
                <ImageIcon size={18} strokeWidth={2.5} />
              ) : (
                <Send size={18} strokeWidth={2.5} />
              )}
            </button>
          </div>
        </div>
        </div>
        <div className="w-full shrink-0" style={{ height: 'env(safe-area-inset-bottom)', backgroundColor: isGeneral ? 'rgba(240, 253, 244, 0.2)' : activeColorHex + '08' }} />
      </div>
    </div>
  );
};

export default memo(ChatView);
